import {
  Flex,
  FormFeedback,
  IconButton,
  SeasonIcon,
  Text,
  Tooltip,
} from '@wyz/ui';
import clsx from 'clsx';
import { get } from 'lodash-es';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { NumberField } from '~/common/components';
import { Trans } from '~/common/context';
import { TranslationKeys } from '~/common/types';
import { DistributionPricingPolicyHelpers } from '~/purchase/helpers';
import { DIAMETER_OPERATORS } from '~/purchase/schemas';
import {
  IAdvancedExtraBonusDraft,
  IDistributionPricingPolicySeason,
  IVehicleTypeColumn,
} from '~/purchase/types';
/* eslint-disable-next-line @wyz/module/no-relative-import -- the sibling is imported directly: going
   through the ~/purchase/components barrel would make this module depend on itself. */
import { AdvancedBonusDiameterRow } from './advanced-bonus-diameter-row';

const {
  advancedCellKey,
  advancedDiameterRows,
  advancedVisibleColumns,
  bonusSign,
  removeAdvancedDiameterRow,
  seasonClass,
  tintClass,
} = DistributionPricingPolicyHelpers;

type Props = {
  draft: IAdvancedExtraBonusDraft;
  seasons: IDistributionPricingPolicySeason[];
  vehicleTypes: IVehicleTypeColumn[];
};

/**
 * The amounts of one bonus: one column group per season, split by vehicle type when that axis is
 * on, and one row per diameter range when that one is. An axis left off collapses to the single
 * column, or the single row, covering every value it could have targeted.
 */
export function AdvancedBonusMatrix({ draft, seasons, vehicleTypes }: Props) {
  const methods = useFormContext<IAdvancedExtraBonusDraft>();
  const diameters = useFieldArray({
    control: methods.control,
    name: 'diameters',
  });

  const columns = advancedVisibleColumns(draft, vehicleTypes);
  const rows = advancedDiameterRows(draft);
  // The diameter column, plus one column per season and vehicle type.
  const seasonColumnCount = seasons.length * columns.length;
  const columnCount = seasonColumnCount + 1;
  // The rows are validated as a whole — two of them may never cover the same diameter — so the
  // violation belongs to the collection rather than to any one row, and react-hook-form files it
  // under its root.
  // The amounts of the rows below the removed one move up with them.
  const removeRow = (index: number) => {
    diameters.remove(index);
    methods.setValue(
      'values',
      removeAdvancedDiameterRow(methods.getValues('values'), index),
    );
  };

  const diametersError = (get(methods.formState.errors, 'diameters.message') ??
    get(methods.formState.errors, 'diameters.root.message')) as
    | keyof TranslationKeys
    | undefined;

  return (
    <div className='advanced-extra-bonus__matrix-wrapper'>
      {/* The diameter column holds three fields once that axis is on, and a label otherwise. */}
      <table
        className={clsx('advanced-extra-bonus__matrix', {
          'advanced-extra-bonus__matrix--diameter': draft.byDiameter,
        })}
      >
        <thead>
          <tr>
            <th rowSpan={2} className='advanced-extra-bonus__matrix-head'>
              <Flex alignItems='center' justifyContent='between'>
                <Trans i18nKey='app.distribution.pricing_policy.detail.advanced.price_by_diameter' />
                {draft.byDiameter && (
                  <Tooltip
                    label={
                      <Trans i18nKey='app.distribution.pricing_policy.detail.advanced.add_diameter' />
                    }
                  >
                    <IconButton
                      id='add-diameter'
                      variant='link'
                      size='xs'
                      onClick={() =>
                        diameters.append({
                          diameter: {
                            operator: DIAMETER_OPERATORS.EQUAL,
                            from: null,
                            to: null,
                          },
                        })
                      }
                      icon='plus'
                    ></IconButton>
                  </Tooltip>
                )}
              </Flex>
            </th>
            {seasons.map((season) => (
              <th
                key={season.id}
                colSpan={columns.length}
                className={`extra-bonus-table__season extra-bonus-table__season--title${seasonClass(season)}`}
              >
                <span className='extra-bonus-table__season-icon'>
                  <SeasonIcon value={season.id} size='xs' />{' '}
                </span>
                <span className='text-uppercase'>
                  <Trans i18nKey={season.i18nKey} />
                </span>
              </th>
            ))}
          </tr>
          <tr>
            {seasons.map((season) =>
              columns.map((column) => (
                <th
                  key={`${season.id}-${column.value}`}
                  className={`extra-bonus-table__tint${tintClass(season)}`}
                >
                  {draft.byVehicleType ? (
                    column.label
                  ) : (
                    <Trans i18nKey='app.distribution.pricing_policy.detail.advanced.every_vehicle_type' />
                  )}
                </th>
              )),
            )}
          </tr>
        </thead>

        <tbody>
          {rows.map((rowIndex) => (
            <tr key={rowIndex}>
              <td className='advanced-extra-bonus__matrix-row-head'>
                {draft.byDiameter ? (
                  <AdvancedBonusDiameterRow
                    rowIndex={rowIndex}
                    onRemoveRow={removeRow}
                  />
                ) : (
                  <Text component='span'>
                    <Trans i18nKey='app.quote.pricing_policy.form.step1.all_diameters' />
                  </Text>
                )}
              </td>

              {seasons.map((season) =>
                columns.map((column) => (
                  <td
                    key={`${season.id}-${column.value}`}
                    className={`extra-bonus-table__tint${tintClass(season)}`}
                  >
                    <NumberField
                      name={`values.${advancedCellKey(season.id, column.value, rowIndex)}`}
                      size='sm'
                      withKnobs={false}
                      sign={bonusSign(draft.bonusType)}
                      feedbackType='tooltip'
                    />
                  </td>
                )),
              )}
            </tr>
          ))}
        </tbody>

        {draft.byDiameter && (
          <tfoot>
            <tr>
              <td colSpan={columnCount}>
                {diametersError && (
                  <FormFeedback invalid id='diameters-error'>
                    <Trans i18nKey={diametersError} />
                  </FormFeedback>
                )}
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}
